import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Film,
  KeyRound,
  ShieldCheck,
  AlertCircle,
  Loader2,
  ArrowRight,
  Clock,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { validateInvitation, redeemInvitation, type ValidateResult } from '@/services/invitationService';
import { useAuth } from '@/hooks/useAuth';
import { AuthModal } from '@/components/auth/AuthModal';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { CinematicIntroModal, CINESYNC_INTRO_SEEN_KEY } from '@/components/common/CinematicIntroModal';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isApprovedHost, refreshProfileStatus } = useAuth();

  // Track if intro has been dismissed for this token in current session
  const [hasDismissedIntro, setHasDismissedIntro] = useState<boolean>(() => {
    try {
      if (!token) return false;
      return Boolean(sessionStorage.getItem(`cinesync_intro_dismissed_${token}`));
    } catch {
      return false;
    }
  });

  const [showIntro, setShowIntro] = useState<boolean>(false);

  const [validationState, setValidationState] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [validationData, setValidationData] = useState<ValidateResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function checkToken() {
      if (!token) {
        setValidationState('invalid');
        setErrorMessage('No invitation link found in the URL.');
        return;
      }

      setValidationState('checking');
      setErrorMessage(null);

      try {
        const result = await validateInvitation(token);
        if (!isMounted) return;

        if (result.valid) {
          setValidationState('valid');
          setValidationData(result);

          // If valid and unauthenticated:
          // Display the cinematic intro video on first arrival for this invitation link
          if (!user) {
            const dismissedInSession = Boolean(sessionStorage.getItem(`cinesync_intro_dismissed_${token}`));
            if (!dismissedInSession) {
              setShowIntro(true);
            } else {
              setIsAuthModalOpen(true);
            }
          }
        } else {
          setValidationState('invalid');
          setErrorMessage(result.error || 'This invitation is invalid, has expired, or has already been used.');
        }
      } catch (err: any) {
        if (!isMounted) return;
        setValidationState('invalid');
        setErrorMessage(err.message || 'Failed to validate invitation.');
      }
    }

    checkToken();

    return () => {
      isMounted = false;
    };
  }, [token, user]);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setHasDismissedIntro(true);
    if (token) {
      try {
        sessionStorage.setItem(`cinesync_intro_dismissed_${token}`, 'true');
        localStorage.setItem(CINESYNC_INTRO_SEEN_KEY, 'true');
      } catch {}
    }
    // Smoothly transition directly into the account creation modal
    if (!user) {
      setIsAuthModalOpen(true);
    }
  };

  const handleRedeemForCurrentAccount = async () => {
    if (!token) return;
    setIsRedeeming(true);
    setErrorMessage(null);

    try {
      const res = await redeemInvitation(token);
      if (res.success) {
        setRedeemSuccess(true);
        await refreshProfileStatus();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setErrorMessage(res.error || 'Could not redeem invitation.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to redeem invitation.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleAuthSuccess = async () => {
    setIsAuthModalOpen(false);
    setRedeemSuccess(true);
    await refreshProfileStatus();
    setTimeout(() => {
      navigate('/');
    }, 1800);
  };

  const formattedExpiry = validationData?.expiresAt
    ? new Date(validationData.expiresAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="min-h-screen bg-cinema-950 text-cinema-100 flex flex-col font-sans selection:bg-lamp/20 selection:text-lamp-bright">
      {/* Top Bar */}
      <header className="hairline-b bg-cinema-900/80 px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-cinema-800 border border-cinema-700 flex items-center justify-center text-lamp shadow-sm">
            <Film className="w-4 h-4" />
          </div>
          <span className="text-sm font-bold tracking-tight text-cinema-100">CineSync</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle showDropdown />
          <button
            onClick={() => navigate('/')}
            className="text-xs font-semibold text-cinema-400 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp px-2.5 py-1.5 rounded"
          >
            Return to Home
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-cinema-900 border border-cinema-700/80 rounded-xl p-8 shadow-cinema-screen">
          {/* Loading State */}
          {validationState === 'checking' && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-8 h-8 text-lamp animate-spin mx-auto" />
              <div>
                <h2 className="text-base font-bold text-cinema-100">Validating Invitation</h2>
                <p className="text-xs text-cinema-400 mt-1">Verifying your invitation…</p>
              </div>
            </div>
          )}

          {/* Invalid / Expired State */}
          {validationState === 'invalid' && (
            <div className="text-center space-y-5">
              <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto text-red-400">
                <AlertCircle className="w-6 h-6" />
              </div>

              <div>
                <h1 className="text-lg font-bold text-cinema-100 tracking-tight">Invitation Invalid</h1>
                <p className="text-xs text-cinema-300 mt-2 leading-relaxed">
                  {errorMessage || 'This invitation link is invalid, has expired, or has already been redeemed.'}
                </p>
              </div>

              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-800 text-left text-xs text-cinema-400 leading-relaxed">
                <p className="font-semibold text-cinema-300 mb-1">Need access?</p>
                CineSync is a private screening website. Ask the administrator to generate a fresh invitation link for you.
              </div>

              <button
                onClick={() => navigate('/')}
                className="w-full py-2.5 px-4 rounded-md bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-xs font-semibold text-cinema-100 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
              >
                Back to Home
              </button>
            </div>
          )}

          {/* Valid Invitation State */}
          {validationState === 'valid' && (
            <div className="space-y-6">
              {redeemSuccess ? (
                <div className="py-6 text-center space-y-4 animate-fade-in">
                  <div className="w-12 h-12 rounded-full bg-reel-emerald/20 border border-reel-emerald/40 flex items-center justify-center mx-auto text-reel-emerald">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-cinema-100">Host Privileges Activated!</h2>
                    <p className="text-xs text-cinema-300 mt-1">
                      Welcome to CineSync! You can now host synchronized watch parties.
                    </p>
                  </div>
                  <div className="text-[11px] text-cinema-500 flex items-center justify-center gap-1.5 pt-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-lamp" />
                    <span>Redirecting to home…</span>
                  </div>
                </div>
              ) : user && isApprovedHost ? (
                /* Already Approved Host */
                <div className="text-center space-y-5">
                  <div className="w-12 h-12 rounded-full bg-reel-emerald/20 border border-reel-emerald/40 flex items-center justify-center mx-auto text-reel-emerald">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-cinema-100">Already Approved Host</h2>
                    <p className="text-xs text-cinema-300 mt-2 leading-relaxed">
                      You are already signed in as <strong className="text-cinema-100 font-medium">{user.email}</strong> and have approved host privileges.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full py-2.5 px-4 rounded-md bg-lamp hover:bg-lamp-bright text-cinema-950 font-bold text-xs tracking-wide transition-all flex items-center justify-center gap-2"
                  >
                    <span>Start a Watch Party</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : user && !isApprovedHost ? (
                /* Signed In but Needs to Redeem */
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-lamp-dim border border-lamp/30 flex items-center justify-center mx-auto text-lamp mb-3">
                      <KeyRound className="w-6 h-6" />
                    </div>
                    <h1 className="text-lg font-bold text-cinema-100 tracking-tight">Activate Host Account</h1>
                    <p className="text-xs text-cinema-400 mt-1">
                      You are signed in as <strong className="text-cinema-100 font-medium">{user.email}</strong>
                    </p>
                  </div>

                  {errorMessage && (
                    <div className="p-3 rounded-lg bg-red-950/50 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {formattedExpiry && (
                    <div className="flex items-center gap-2 text-[11px] text-cinema-400 bg-cinema-950 px-3 py-2 rounded-lg border border-cinema-800">
                      <Clock className="w-3.5 h-3.5 text-lamp" />
                      <span>Expires on {formattedExpiry}</span>
                    </div>
                  )}

                  <button
                    onClick={handleRedeemForCurrentAccount}
                    disabled={isRedeeming}
                    className="w-full py-3 px-4 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-50 text-cinema-950 font-bold text-xs tracking-wide transition-all duration-150 enabled:active:scale-[0.96] flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                  >
                    {isRedeeming ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Activating Host Status…</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        <span>Redeem & Activate Host Access</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Unauthenticated Friend */
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-lamp-dim border border-lamp/30 flex items-center justify-center mx-auto text-lamp mb-3">
                      <KeyRound className="w-6 h-6" />
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lamp/15 text-lamp text-[10px] font-mono font-semibold uppercase border border-lamp/30 mb-2">
                      <Sparkles className="w-3 h-3" />
                      <span>VIP Screening Invite</span>
                    </div>
                    <h1 className="text-lg font-bold text-cinema-100 tracking-tight">You're Invited to CineSync</h1>
                    <p className="text-xs text-cinema-300 mt-2 leading-relaxed">
                      You have been invited to join this private screening club as an approved room host.
                    </p>
                  </div>

                  {formattedExpiry && (
                    <div className="flex items-center gap-2 text-[11px] text-cinema-400 bg-cinema-950 px-3 py-2 rounded-lg border border-cinema-800">
                      <Clock className="w-3.5 h-3.5 text-lamp" />
                      <span>Invitation valid until {formattedExpiry}</span>
                    </div>
                  )}

                  <div className="space-y-2 pt-2">
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="w-full py-3 px-4 rounded-md bg-lamp hover:bg-lamp-bright text-cinema-950 font-bold text-xs tracking-wide transition-all duration-150 enabled:active:scale-[0.96] shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                    >
                      <KeyRound className="w-4 h-4" />
                      <span>Accept Invitation & Register</span>
                    </button>

                    <p className="text-center text-[11px] text-cinema-500 pt-1">
                      Sign up with Google or Email. Single-use invitation.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Fullscreen Cinematic Entrance (Plays once automatically on arrival) */}
      <CinematicIntroModal
        isOpen={showIntro && !user && validationState === 'valid'}
        onComplete={handleIntroComplete}
      />

      {/* Auth Modal with Invitation Token */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
        invitationToken={token}
        initialMode="signup"
      />
    </div>
  );
}
